import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Modal, Spinner } from 'react-bootstrap';
import careReferralCatalogService from '../../../services/careReferralCatalogService';
import carePartnerService from '../../../services/carePartnerService';
import Icon from '../../common/Icons';
import {
  MEDICAL_COVERAGE_TYPES,
  REFERRAL_TABS,
  buildEmptyReferralForm,
  getCareReferralCategoryConfig
} from '../../../constants/careReferralCategories';
import { partnerTypeForCategory } from '../../../constants/carePartnerTypes';
import { useDoctorWorkspace } from '../../doctor/doctorWorkspaceContext';
import { computeFloatingMenuPosition, estimateActionMenuHeight } from '../../../utils/floatingMenuPosition';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorPageHeader } from '../ui';
import CareReferralListItem from './CareReferralListItem';
import { getReferralCoveragePillProps, REFERRAL_TAB_SHORT } from './careReferralListDisplay';
import '../HeadDoctorDashboard.css';

const COVERAGE_PILL_LABEL = {
  SMIE: 'SMIE',
  CMC: 'CMC'
};

export default function CareReferralCatalogManager() {
  const copy = HEAD_DOCTOR_COPY.REFERRAL_CATALOG;
  const { toast } = useDoctorWorkspace();

  const [activeTab, setActiveTab] = useState(REFERRAL_TABS[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedCoverageType, setSelectedCoverageType] = useState(MEDICAL_COVERAGE_TYPES.SMIE.id);
  const [form, setForm] = useState(() => buildEmptyReferralForm(REFERRAL_TABS[0]));
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionMenuItem, setActionMenuItem] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0, placement: 'below' });

  const categoryConfig = useMemo(() => getCareReferralCategoryConfig(activeTab), [activeTab]);

  const loadPartners = useCallback(async () => {
    const partnerType = partnerTypeForCategory(activeTab);
    if (!partnerType) {
      setPartners([]);
      return;
    }
    try {
      const res = await carePartnerService.list({ partner_type: partnerType, is_active: 'true' });
      setPartners(res.success && Array.isArray(res.data) ? res.data : []);
    } catch {
      setPartners([]);
    }
  }, [activeTab]);

  const loadItems = useCallback(async () => {
    if (!categoryConfig) return;

    setLoading(true);
    setError('');
    try {
      const params = {
        category: activeTab,
        coverage_type: selectedCoverageType
      };
      if (search.trim()) params.search = search.trim();
      if (!showInactive) params.is_active = 'true';

      const res = await careReferralCatalogService.list(params);
      if (res.success) {
        setItems(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.message || 'Chargement impossible');
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [activeTab, categoryConfig, search, selectedCoverageType, showInactive]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadItems();
    loadPartners();
  }, [loadItems, loadPartners]);

  useEffect(() => {
    setForm(buildEmptyReferralForm(activeTab));
    setEditingId(null);
    setActionMenuItem(null);
  }, [activeTab]);

  const stats = useMemo(
    () => ({
      count: items.length,
      active: items.filter((i) => i.is_active).length,
      inactive: items.filter((i) => !i.is_active).length
    }),
    [items]
  );

  const hasActiveFilters =
    Boolean(searchInput.trim()) ||
    showInactive ||
    selectedCoverageType !== MEDICAL_COVERAGE_TYPES.SMIE.id;

  const resetFilters = () => {
    setSearchInput('');
    setShowInactive(false);
    setSelectedCoverageType(MEDICAL_COVERAGE_TYPES.SMIE.id);
  };

  const openActionMenu = (event, item) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPos(
      computeFloatingMenuPosition(rect, {
        menuWidth: 220,
        menuHeight: estimateActionMenuHeight(2, { withSeparator: true })
      })
    );
    setActionMenuItem(item);
  };

  const closeActionMenu = () => setActionMenuItem(null);

  const resetForm = () => {
    setForm(buildEmptyReferralForm(activeTab));
    setEditingId(null);
  };

  const openCreate = () => {
    closeActionMenu();
    resetForm();
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      description: item.description || '',
      coverage_type: item.coverage_type || MEDICAL_COVERAGE_TYPES.SMIE.id,
      default_partner_id: item.default_partner_id || '',
      is_active: Boolean(item.is_active),
      metadata: { ...buildEmptyReferralForm(activeTab).metadata, ...(item.metadata || {}) }
    });
    setModalError('');
    setShowModal(true);
    closeActionMenu();
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setModalError('');
    resetForm();
  };

  const updateMetadata = (key, value) => {
    setForm((prev) => ({ ...prev, metadata: { ...prev.metadata, [key]: value } }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!categoryConfig) return;

    setSaving(true);
    setModalError('');
    try {
      const payload = {
        category: activeTab,
        name: form.name.trim(),
        description: form.description.trim() || null,
        coverage_type: form.coverage_type,
        default_partner_id: form.default_partner_id || null,
        is_active: Boolean(form.is_active),
        metadata: form.metadata
      };
      if (!payload.name) throw new Error('Le libellé est obligatoire.');

      if (editingId) {
        await careReferralCatalogService.update(editingId, payload);
        toast('success', 'Acte mis à jour.');
      } else {
        await careReferralCatalogService.create(payload);
        toast('success', 'Acte ajouté au catalogue.');
      }
      closeModal();
      await loadItems();
    } catch (err) {
      setModalError(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer « ${item.name} » du catalogue ?`)) return;
    closeActionMenu();
    try {
      await careReferralCatalogService.remove(item.id);
      toast('success', 'Acte supprimé.');
      if (editingId === item.id) closeModal();
      await loadItems();
    } catch (err) {
      setError(err.message || 'Suppression impossible');
    }
  };

  const renderCoveragePill = (coverageType) => {
    const pill = getReferralCoveragePillProps(coverageType);
    return <span className={pill.className}>{pill.label}</span>;
  };

  const isEditing = Boolean(editingId);
  const showPreview = Boolean(form.name.trim());
  const previewPartner = partners.find(
    (p) => String(p.id) === String(form.default_partner_id)
  );

  if (!categoryConfig) {
    return (
      <Alert variant="danger" className="m-3">
        Catégorie d&apos;orientation inconnue.
      </Alert>
    );
  }

  return (
    <div className="doctor-dashboard recv-dash-pro--main hd-referral-page">
      <HeadDoctorPageHeader
        badge={copy.BADGE}
        title={categoryConfig.pageTitle}
        description={categoryConfig.pageSubtitle}
        actions={
          <button type="button" className="hd-co-create-btn" onClick={openCreate}>
            <Icon name="plus" size={16} aria-hidden />
            {copy.NEW_BUTTON}
          </button>
        }
      />

      <nav className="hd-referral-category-nav" aria-label={copy.FILTER_CATEGORY}>
        <div className="hd-referral-category-nav__track" role="tablist">
          {REFERRAL_TABS.map((tabId) => {
            const config = getCareReferralCategoryConfig(tabId);
            return (
              <button
                key={tabId}
                type="button"
                role="tab"
                aria-selected={activeTab === tabId}
                className={`hd-referral-category-nav__btn${
                  activeTab === tabId ? ' is-active' : ''
                }`}
                onClick={() => setActiveTab(tabId)}
              >
                {REFERRAL_TAB_SHORT[tabId] || config?.label}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="hd-co-shell">
        <section className="hd-co-stats hd-co-stats--3" aria-label="Synthèse du catalogue">
          <article className="hd-co-stat-card">
            <span className="hd-co-stat-card__label">{copy.STAT_SHOWN}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.count}</span>
            <span className="hd-co-stat-card__hint">{copy.RESULTS_LABEL}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--success">
            <span className="hd-co-stat-card__label">{copy.STAT_ACTIVE}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.active}</span>
          </article>
          <article className="hd-co-stat-card hd-co-stat-card--queue">
            <span className="hd-co-stat-card__label">{copy.STAT_INACTIVE}</span>
            <span className="hd-co-stat-card__value">{loading ? '—' : stats.inactive}</span>
          </article>
        </section>

        <div className="hd-co-toolbar">
          <div className="hd-co-search-wrap">
            <Icon name="search" size={16} className="hd-co-search-icon" aria-hidden />
            <input
              type="search"
              className="hd-co-search-input"
              placeholder={copy.SEARCH_PLACEHOLDER}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={copy.SEARCH_PLACEHOLDER}
            />
          </div>
          <div className="hd-co-toolbar-end">
            <div className="hd-co-filters" role="group" aria-label={copy.FILTER_COVERAGE}>
              {Object.values(MEDICAL_COVERAGE_TYPES).map((coverage) => (
                <button
                  key={coverage.id}
                  type="button"
                  className={`hd-co-filter-pill${
                    selectedCoverageType === coverage.id ? ' is-active' : ''
                  }`}
                  onClick={() => setSelectedCoverageType(coverage.id)}
                >
                  {COVERAGE_PILL_LABEL[coverage.id] || coverage.id}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="hd-co-refresh-btn"
              onClick={loadItems}
              disabled={loading}
              aria-label={copy.REFRESH_ARIA}
              title={copy.REFRESH_ARIA}
            >
              <Icon name="refresh" size={18} aria-hidden />
            </button>
            <div className="hd-co-toolbar-meta">
              <strong>{loading ? '—' : stats.count}</strong>
              <span>{copy.RESULTS_LABEL}</span>
            </div>
          </div>
        </div>

        <div className="hd-exam-filter-bar">
          <div className="hd-exam-filter-bar__end hd-exam-filter-bar__end--solo">
            <label className="hd-exam-inactive-toggle">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span>{copy.FILTER_INACTIVE}</span>
            </label>
            {hasActiveFilters ? (
              <button type="button" className="hd-co-reset-btn" onClick={resetFilters}>
                {copy.FILTER_RESET}
              </button>
            ) : null}
          </div>
        </div>

        {error ? <Alert variant="danger" className="hd-co-alert mb-0">{error}</Alert> : null}

        {loading ? (
          <div className="hd-co-state" role="status" aria-live="polite">
            <Spinner animation="border" className="hd-co-state__spinner" />
            <p className="hd-co-state__text mb-0">{copy.LOADING}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="hd-co-state hd-co-state--empty">
            <div className="hd-co-state__icon" aria-hidden>
              <Icon name="arrowRight" size={32} />
            </div>
            <h3 className="hd-co-state__title">{copy.EMPTY_TITLE}</h3>
            <p className="hd-co-state__text">{copy.EMPTY}</p>
            <button type="button" className="hd-co-create-btn" onClick={openCreate}>
              <Icon name="plus" size={16} aria-hidden />
              {copy.NEW_BUTTON}
            </button>
          </div>
        ) : (
          <ul className="hd-referral-list" aria-label={copy.LIST_ARIA}>
            {items.map((item) => (
              <CareReferralListItem
                key={item.id}
                item={item}
                categoryConfig={categoryConfig}
                isEditing={showModal && editingId === item.id}
                copy={copy}
                onOpenMenu={(e) => openActionMenu(e, item)}
              />
            ))}
          </ul>
        )}
      </div>

      {actionMenuItem ? (
        <>
          <button
            type="button"
            className="hd-exam-actions-menu__backdrop"
            aria-label="Fermer le menu actions"
            onClick={closeActionMenu}
          />
          <div
            className={`hd-exam-actions-menu__floating${
              actionMenuPos.placement === 'above' ? ' hd-exam-actions-menu__floating--above' : ''
            }`}
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
            role="menu"
          >
            <button
              type="button"
              className="hd-exam-actions-menu__item"
              role="menuitem"
              onClick={() => openEdit(actionMenuItem)}
            >
              <Icon name="edit" size={15} />
              <span>{copy.ACTION_EDIT}</span>
            </button>
            <div className="hd-exam-actions-menu__separator" />
            <button
              type="button"
              className="hd-exam-actions-menu__item hd-exam-actions-menu__danger"
              role="menuitem"
              onClick={() => remove(actionMenuItem)}
            >
              <Icon name="trash" size={15} />
              <span>{copy.ACTION_DELETE}</span>
            </button>
          </div>
        </>
      ) : null}

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        size="lg"
        className="hd-dis-modal-root hd-referral-modal-root"
        backdropClassName="hd-dis-modal-backdrop"
        dialogClassName="hd-dis-modal-dialog"
        contentClassName="hd-dis-modal__content"
        backdrop={saving ? 'static' : true}
        keyboard={!saving}
      >
        <div className="hd-dis-modal__header">
          <div className="hd-dis-modal__header-main">
            <span className="hd-dis-modal__icon" aria-hidden>
              <Icon name="arrowRight" size={22} />
            </span>
            <div className="hd-dis-modal__titles">
              <span
                className={`hd-dis-modal__mode${
                  isEditing ? ' hd-dis-modal__mode--edit' : ' hd-dis-modal__mode--new'
                }`}
              >
                {isEditing ? copy.MODAL_MODE_EDIT : copy.MODAL_MODE_NEW}
              </span>
              <h2 className="hd-dis-modal__title">
                {isEditing ? copy.MODAL_TITLE_EDIT : copy.MODAL_TITLE_NEW}
              </h2>
              <p className="hd-dis-modal__subtitle mb-0">
                {categoryConfig.label} —{' '}
                {isEditing ? copy.MODAL_SUBTITLE_EDIT : copy.MODAL_SUBTITLE_NEW}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="hd-dis-modal__close"
            onClick={closeModal}
            aria-label={copy.MODAL_CLOSE_ARIA}
          >
            <Icon name="times" size={18} aria-hidden />
          </button>
        </div>

        <Form onSubmit={submit}>
          <div className="hd-dis-modal__body">
            {modalError ? (
              <Alert variant="danger" className="hd-dis-modal__alert mb-0">
                {modalError}
              </Alert>
            ) : null}

            <div className="hd-dis-modal__fields">
              <div className="hd-dis-modal__field">
                <label className="hd-dis-modal__label" htmlFor="hd-referral-name">
                  {copy.FIELD_LABEL} *
                </label>
                <input
                  id="hd-referral-name"
                  type="text"
                  className="hd-dis-modal__input"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={copy.FIELD_LABEL_PLACEHOLDER}
                  maxLength={255}
                  required
                  autoFocus
                  autoComplete="off"
                />
              </div>

              <div className="hd-dis-modal__field-grid">
                <div className="hd-dis-modal__field">
                  <label className="hd-dis-modal__label" htmlFor="hd-referral-coverage">
                    {copy.FIELD_COVERAGE}
                  </label>
                  <select
                    id="hd-referral-coverage"
                    className="hd-dis-modal__input hd-dis-modal__select"
                    value={form.coverage_type}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, coverage_type: e.target.value }))
                    }
                  >
                    {Object.values(MEDICAL_COVERAGE_TYPES).map((coverage) => (
                      <option key={coverage.id} value={coverage.id}>
                        {coverage.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="hd-dis-modal__field">
                  <label className="hd-dis-modal__label" htmlFor="hd-referral-partner">
                    {copy.FIELD_PARTNER}
                  </label>
                  <select
                    id="hd-referral-partner"
                    className="hd-dis-modal__input hd-dis-modal__select"
                    value={form.default_partner_id}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, default_partner_id: e.target.value }))
                    }
                  >
                    <option value="">{copy.FIELD_PARTNER_NONE}</option>
                    {partners.map((partner) => (
                      <option key={partner.id} value={partner.id}>
                        {partner.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="hd-dis-modal__field hd-dis-modal__field--full">
                <label className="hd-dis-modal__label" htmlFor="hd-referral-description">
                  {copy.FIELD_DESCRIPTION}
                </label>
                <textarea
                  id="hd-referral-description"
                  className="hd-dis-modal__textarea"
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder={copy.FIELD_DESCRIPTION_PLACEHOLDER}
                />
              </div>

              {(categoryConfig.metadataFields || []).map((field) => (
                <div className="hd-dis-modal__field" key={field.key}>
                  <label className="hd-dis-modal__label" htmlFor={`hd-referral-meta-${field.key}`}>
                    {field.label}
                  </label>
                  <input
                    id={`hd-referral-meta-${field.key}`}
                    type="text"
                    className="hd-dis-modal__input"
                    value={form.metadata?.[field.key] || ''}
                    onChange={(e) => updateMetadata(field.key, e.target.value)}
                    placeholder={field.placeholder || ''}
                    maxLength={field.maxLength}
                  />
                </div>
              ))}

              <div className="hd-dis-modal__field hd-dis-modal__field--full">
                <label className="hd-exam-inactive-toggle hd-partner-active-toggle">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                  />
                  <span>{copy.FIELD_ACTIVE}</span>
                </label>
              </div>
            </div>

            {showPreview ? (
              <div className="hd-dis-modal__preview hd-referral-modal__preview" aria-live="polite">
                <span className="hd-dis-modal__preview-label">{copy.MODAL_PREVIEW_LABEL}</span>
                <div className="hd-referral-modal__preview-row">
                  <span className="hd-referral-list__title">{form.name.trim()}</span>
                  {renderCoveragePill(form.coverage_type)}
                  {previewPartner ? (
                    <span className="hd-referral-modal__preview-partner">{previewPartner.name}</span>
                  ) : null}
                </div>
                {form.description.trim() ? (
                  <span className="hd-referral-modal__preview-desc">{form.description.trim()}</span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="hd-dis-modal__footer">
            <button
              type="button"
              className="hd-dis-modal__btn-cancel"
              onClick={closeModal}
              disabled={saving}
            >
              {copy.BTN_CANCEL}
            </button>
            <button type="submit" className="hd-co-create-btn" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  {copy.BTN_SAVE}
                </>
              ) : isEditing ? (
                copy.BTN_UPDATE
              ) : (
                <>
                  <Icon name="plus" size={16} aria-hidden />
                  {copy.BTN_CREATE}
                </>
              )}
            </button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
